/**
 * Pure logic for the temperature page (/data/suhuN live, /history/temperature
 * per day). No Firebase in here -- the page fetches, this file interprets.
 * Wire values are x10 °C; -1270 means the probe is disconnected.
 */
import { Row, TEMP_DISCONNECTED, dayStart } from "./history";

/** A row further than this from its predecessor breaks the line. */
export const MAX_GAP_S = 90;
export const MAX_SENSORS = 6;
export const DEFAULT_THRESHOLDS = { warn: 45, crit: 55 };

export type Range = "1h" | "6h" | "24h" | "7d";
export const RANGE_S: Record<Range, number> = {
  "1h": 3600,
  "6h": 6 * 3600,
  "24h": 86400,
  "7d": 7 * 86400,
};

export interface LiveSensor {
  /** 1-based index, i.e. the N of suhuN / uN. */
  index: number;
  /** °C, or null when the probe is disconnected / not a number. */
  value: number | null;
}

/** Only the suhuN keys that exist (N = 1..6), in index order. */
export function liveSensors(data: unknown): LiveSensor[] {
  if (!data || typeof data !== "object") return [];
  const src = data as Record<string, unknown>;
  const out: LiveSensor[] = [];
  for (let i = 1; i <= MAX_SENSORS; i++) {
    if (!(`suhu${i}` in src)) continue;
    const raw = src[`suhu${i}`];
    const ok =
      typeof raw === "number" && Number.isFinite(raw) && raw !== TEMP_DISCONNECTED;
    out.push({ index: i, value: ok ? (raw as number) / 10 : null });
  }
  return out;
}

/** /history/temperature/sensors -> { "1": "28FF..", ... } keyed by index. */
export function parseSensors(val: unknown): Record<number, string> {
  const out: Record<number, string> = {};
  if (!val || typeof val !== "object") return out;
  for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
    const m = /^u(\d+)$/.exec(k);
    if (m && typeof v === "string" && v) out[Number(m[1])] = v.toUpperCase();
  }
  return out;
}

/** Name the user gave, else "Sensor <last 4 hex>", else "Sensor <index>". */
export function sensorLabel(
  index: number,
  roms: Record<number, string>,
  names: Record<string, string>
): string {
  const rom = roms[index];
  if (!rom) return `Sensor ${index}`;
  return names[rom]?.trim() || `Sensor ${rom.slice(-4)}`;
}

export type Level = "ok" | "warn" | "crit";
export function levelOf(
  c: number | null,
  t: { warn: number; crit: number }
): Level {
  if (c === null) return "ok";
  return c >= t.crit ? "crit" : c >= t.warn ? "warn" : "ok";
}

/** [from, to) in Unix seconds. `day` is a WIB "YYYYMMDD". */
export function windowFor(
  range: Range,
  day: string,
  today: string,
  nowS: number
): [number, number] {
  const len = RANGE_S[range];
  if (range === "7d") return [dayStart(today) + 86400 - len, nowS];
  const end = day === today ? nowS : dayStart(day) + 86400;
  return [range === "24h" && day !== today ? dayStart(day) : end - len, end];
}

/** Which of `days` (WIB buckets) overlap [from, to]. */
export function daysFor(from: number, to: number, days: string[]): string[] {
  return days.filter((d) => {
    const s = dayStart(d);
    return s + 86400 > from && s <= to;
  });
}

export interface SeriesPoint {
  t: number;
  /** 1 on the bare point that breaks the line after a gap; such a point carries no data. */
  gap?: number;
  [key: string]: number | undefined;
}

/** u1..u6 (DS18B20) and t1 (teras DHT) are temperatures; rh1 is humidity. */
export const isTempKey = (k: string) => /^[ut]\d+$/.test(k);
export const isHumKey = (k: string) => /^rh\d+$/.test(k);
export const TERAS_NAME_KEY = "teras:t1";

/** Label for any series key: DS18B20 by ROM name, teras by its fixed key. */
export function seriesLabel(
  key: string,
  roms: Record<number, string>,
  names: Record<string, string>
): string {
  if (/^u\d+$/.test(key)) return sensorLabel(Number(key.slice(1)), roms, names);
  if (isHumKey(key)) return "Kelembapan Teras";
  return names[TERAS_NAME_KEY]?.trim() || "Teras";
}

/**
 * Rows in [from, to] as chart points keyed "u1".. . A missing or disconnected
 * reading stays undefined so the line breaks; a bare point is inserted after a
 * gap > MAX_GAP_S so the line is not drawn across it.
 */
export function buildSeries(rows: Row[], from: number, to: number): SeriesPoint[] {
  const out: SeriesPoint[] = [];
  let prev: number | undefined;
  for (const r of rows) {
    if (r.t < from || r.t > to) continue;
    if (prev !== undefined && r.t - prev > MAX_GAP_S) out.push({ t: prev + 1, gap: 1 });
    out.push({ t: r.t, ...r.temps, ...r.hum });
    prev = r.t;
  }
  return out;
}

export interface Stats {
  min: number;
  max: number;
  avg: number;
}

export function statsFor(points: SeriesPoint[], key: string): Stats | null {
  let n = 0;
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    const v = p[key];
    if (v === undefined) continue;
    n++;
    sum += v;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return n ? { min, max, avg: sum / n } : null;
}

/** Temperature keys (u*, t*) present anywhere in the points, u1..u6 first. */
export function sensorKeys(points: SeriesPoint[]): string[] {
  return keysWhere(points, isTempKey);
}

export function humKeys(points: SeriesPoint[]): string[] {
  return keysWhere(points, isHumKey);
}

function keysWhere(points: SeriesPoint[], ok: (k: string) => boolean): string[] {
  const set = new Set<string>();
  for (const p of points) for (const k of Object.keys(p)) if (ok(k)) set.add(k);
  return Array.from(set).sort((a, b) => {
    // "t1" sorts after every "uN"; otherwise by index.
    const wa = a[0] === "u" ? 0 : 1;
    const wb = b[0] === "u" ? 0 : 1;
    return wa - wb || Number(a.replace(/\D/g, "")) - Number(b.replace(/\D/g, ""));
  });
}

/**
 * One timeline from sources sampled at different rates (30 s sensors, 5 s teras).
 * Each series is held at its last reading for at most MAX_GAP_S, so a slower
 * source does not break the line between its own samples, while a real outage
 * (or a disconnected reading) still does. Pass the per-source `buildSeries` output.
 */
export function mergeSeries(parts: SeriesPoint[][]): SeriesPoint[] {
  const keysOf = parts.map((p) => keysWhere(p, (k) => isTempKey(k) || isHumKey(k)));
  const rows = parts
    .flatMap((p, src) => p.filter((pt) => !pt.gap).map((pt) => ({ src, pt })))
    .sort((a, b) => a.pt.t - b.pt.t);
  const held: Record<string, { v?: number; t: number }> = {};
  const out: SeriesPoint[] = [];
  for (const { src, pt } of rows) {
    for (const k of keysOf[src]) held[k] = { v: pt[k], t: pt.t };
    const prev = out.length ? out[out.length - 1].t : undefined;
    if (prev !== undefined && pt.t - prev > MAX_GAP_S) out.push({ t: prev + 1, gap: 1 });
    const point: SeriesPoint = { t: pt.t };
    for (const k of Object.keys(held))
      if (pt.t - held[k].t <= MAX_GAP_S && held[k].v !== undefined) point[k] = held[k].v;
    out.push(point);
  }
  return out;
}
